import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Eye } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Product } from '@/types/product';
import { useLanguage } from '@/context/LanguageContext';

interface ProductCardProps {
  product: Product;
  onClick?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const { addToCart } = useCart();
  const { tl } = useLanguage();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const primaryImage = product.images?.[0] || product.image_nobg || '/placeholder.svg';
  const secondaryImage =
    product.images?.[1] && product.images[1] !== primaryImage
      ? product.images[1]
      : product.image_nobg && product.image_nobg !== primaryImage
        ? product.image_nobg
        : null;

  return (
    <div className="group animate-scale-in cursor-pointer bg-white" onClick={onClick}>
      <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
        <img
          src={primaryImage}
          alt={product.name}
          className="h-full w-full object-cover transition-opacity duration-500 ease-out"
        />
        {secondaryImage && (
          <img
            src={secondaryImage}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
          />
        )}

        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/35 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <button
            onClick={handleAddToCart}
            className="bg-white p-3 text-black transition-colors hover:bg-black hover:text-white"
            aria-label={tl('Ajouter au panier', 'Add to cart', 'Aggiungi al carrello')}
          >
            <ShoppingCart size={20} />
          </button>
          <Link
            to={`/product/${product.id}`}
            className="bg-white p-3 text-black transition-colors hover:bg-black hover:text-white"
            aria-label={tl('Voir le produit', 'View product', 'Vedi prodotto')}
            onClick={(e) => e.stopPropagation()}
          >
            <Eye size={20} />
          </Link>
        </div>

        <div className="absolute left-0 top-0 flex flex-col items-start gap-1">
          {product.discount != null && product.discount > 0 && (
            <span className="bg-red-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              -{product.discount}%
            </span>
          )}
          {product.new && (
            <span className="bg-neutral-800 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              {tl('NOUVEAU', 'NEW', 'NUOVO')}
            </span>
          )}
          {product.collection_2026 && (
            <span className="bg-furniture-gold px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-black">
              {tl('COLLECTION 2026', '2026 COLLECTION', 'COLLEZIONE 2026')}
            </span>
          )}
          {product.featured && (
            <span className="bg-furniture-wine px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
              {tl('EN TENDANCE', 'IN TREND', 'DI TENDENZA')}
            </span>
          )}
        </div>
      </div>

      <div className="pt-4 text-left">
        <Link to={`/product/${product.id}`} className="block">
          <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-black transition-colors hover:text-neutral-600">
            {product.name}
          </h3>
          <p className="mt-1 text-xs text-neutral-500">{product.category}</p>
          <div className="mt-2 flex items-center justify-between text-black">
            <span className="text-xs font-medium uppercase tracking-wider">
              {tl('Prix sur demande', 'Price on request', 'Prezzo su richiesta')}
            </span>
            {(!product.stock || product.stock <= 0) && (
              <span className="text-xs text-destructive">
                {tl('Rupture de stock', 'Out of stock', 'Esaurito')}
              </span>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default ProductCard;
